import type { CloudInventory, SelectedEntity } from "../../ctxReflectorV2/components/BGP/BgpVisibilityData";
import type { InsightSourceType } from "../types/insightApi";

/**
 * Canonical identity contract (CTX-97 "real-data milestone" correction): the
 * sidebar's incremental `id` (e.g. "110") is a display id, not necessarily
 * the discovery object key. `subId` is the field expected to carry the
 * canonical CTXR UUID for both Reflectors and Clusters — this mirrors the
 * exact fallback `BgpVisibilityPage.fetchNeighborData` already uses
 * ("Use subId for both reflectors and clusters (fallback to id if subId not
 * available)"). Until the CTX-99 backend contract can be exercised locally,
 * this stays the single translation point — never guessed inline elsewhere.
 */
export function canonicalSourceId(entity: Pick<SelectedEntity, "id" | "subId">): string {
	return entity.subId || entity.id;
}

/** Reflector/Cluster leaf as it appears inside a `CloudInventory` region. */
export type InventoryReflector = CloudInventory["clouds"][number]["accounts"][number]["regions"][number]["reflectors"][number];
export type InventoryCluster = CloudInventory["clouds"][number]["accounts"][number]["regions"][number]["clusters"][number];

const EMPTY_INVENTORY: CloudInventory = { clouds: [] };

/**
 * Filters the shared BGP `CloudInventory` tree by free-text search, without
 * mutating the canonical data model or fetch pipeline. Standalone Reflectors
 * and Clusters both survive filtering and are rendered side by side in each
 * region (see `InsightScopeBrowser`) — the tree is fetched once via
 * `useCloudInventorySource` and searching never triggers a second request.
 *
 * Search matches cloud name, account id/name, region name, and the visible
 * entity name/id/subId at every level; an ancestor is kept whenever it (or
 * any descendant) matches, so a hit deep in the tree still surfaces its full
 * path.
 */
export function filterInventoryForInsight(
	inventory: CloudInventory | null,
	search: string
): CloudInventory {
	if (!inventory) return EMPTY_INVENTORY;
	const needle = search.trim().toLowerCase();

	const matchesEntity = (entity: InventoryReflector | InventoryCluster): boolean => {
		if (!needle) return true;
		return (
			entity.display_name.toLowerCase().includes(needle) ||
			entity.id.toLowerCase().includes(needle) ||
			(entity.subId ?? "").toLowerCase().includes(needle)
		);
	};

	// With no active search, every Cloud/Account/Region is kept regardless of
	// whether it has entities in the current mode — that is what lets an
	// empty region/account render its own "No reflectors/clusters here" state
	// (per-level empty states) instead of silently vanishing from the tree.
	// A search prunes any branch that neither matches by label nor contains a
	// matching leaf, while an ancestor-level match (cloud/account/region name)
	// keeps its whole subtree — including entities that don't themselves
	// match the needle — so a location hit still shows what's actually there.
	const clouds = inventory.clouds.flatMap((cloud) => {
		const cloudMatches = !needle || cloud.name.toLowerCase().includes(needle);

		const accounts = cloud.accounts.flatMap((account) => {
			const accountMatches =
				!needle || cloudMatches || account.name.toLowerCase().includes(needle) || account.id.toLowerCase().includes(needle);

			const regions = account.regions.flatMap((region) => {
				const regionMatches = !needle || accountMatches || region.name.toLowerCase().includes(needle);
				// Standalone Reflectors and Clusters are filtered independently but
				// kept side by side: the browser lists both in one region, the way
				// BGP Visibility does, so a tenant whose reflectors are deployed as
				// HA clusters is never presented with an apparently empty region.
				const reflectors = regionMatches ? region.reflectors : region.reflectors.filter(matchesEntity);
				const clusters = regionMatches ? region.clusters : region.clusters.filter(matchesEntity);
				const keepRegion = !needle || regionMatches || reflectors.length + clusters.length > 0;
				if (!keepRegion) return [];
				return [{ ...region, reflectors, clusters }];
			});

			const keepAccount = !needle || accountMatches || regions.length > 0;
			if (!keepAccount) return [];
			return [{ ...account, regions }];
		});

		const keepCloud = !needle || cloudMatches || accounts.length > 0;
		if (!keepCloud) return [];
		return [{ ...cloud, accounts }];
	});

	return { clouds };
}

/** Total Reflector/Cluster leaf count across an (already mode-filtered) tree — used for the "no sources" empty state. */
export function countLeaves(inventory: CloudInventory): number {
	let count = 0;
	inventory.clouds.forEach((cloud) =>
		cloud.accounts.forEach((account) =>
			account.regions.forEach((region) => {
				count += region.reflectors.length + region.clusters.length;
			})
		)
	);
	return count;
}

export interface EntityPath {
	cloudId: string;
	accountId: string;
	regionId: string;
}

/**
 * Locates the Cloud/Account/Region ancestry of the entity whose canonical id
 * matches `sourceId`, so a deep-linked/refreshed active selection can
 * auto-expand its path in the tree without the user re-navigating to it by
 * hand. Searches the unfiltered inventory (not the search/mode-filtered
 * view) since the active selection must be findable regardless of the
 * current search text.
 */
export function findEntityPath(inventory: CloudInventory | null, mode: InsightSourceType, sourceId: string): EntityPath | null {
	if (!inventory) return null;
	for (const cloud of inventory.clouds) {
		for (const account of cloud.accounts) {
			for (const region of account.regions) {
				const entities = mode === "reflector" ? region.reflectors : region.clusters;
				if (entities.some((entity) => canonicalSourceId(entity) === sourceId)) {
					return { cloudId: cloud.id, accountId: account.id, regionId: region.id };
				}
			}
		}
	}
	return null;
}
